// src/utils/bcpValidation.ts - Validation utilities for BCP form
import { FormState, ValidationErrors, AdditionalLocation } from '../types/bcpForm';
import { FieldChangeHandler, FieldBlurHandler, ValidationRules, FormFieldValue } from '../types/common';

// Validation rules for BCP form
export const validationRules: ValidationRules = {
  business_name: (value: string) => value.trim().length < 2 ? 'Business name must be at least 2 characters' : '',
  business_type: (value: string) => !value.trim() ? 'Business type is required' : '',
  industry: (value: string) => !value.trim() ? 'Industry is required' : '',
  employee_count: (value: number) => !value || value <= 0 ? 'Employee count is required' : value > 10000 ? 'Employee count seems too high' : '',
  annual_revenue: (value: string) => '',
  'primary_location.address': (value: string) => !value.trim() ? 'Primary address is required' : '',
  'primary_location.city': (value: string) => !value.trim() ? 'Primary city is required' : '',
  'primary_location.state': (value: string) => !value.trim() ? 'Primary state is required' : '',
  'primary_location.zip': (value: string) => !value.trim() ? 'Primary zip code is required' : '',
};

// Validate a single field
export const validateField = (field: string, value: FormFieldValue): string => {
  const rule = validationRules[field as keyof typeof validationRules];
  return rule ? rule(value) : '';
};

// Validate all fields in the form
export const validateForm = (formData: FormState): ValidationErrors => {
  const newErrors: ValidationErrors = {};

  // Validate basic fields
  Object.keys(validationRules).forEach(field => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      const parentValue = formData[parent as keyof typeof formData];
      if (typeof parentValue === 'object' && parentValue !== null) {
        const value = (parentValue as any)[child];
        const error = validateField(field, value);
        if (error) newErrors[field] = error;
      }
    } else {
      const value = formData[field as keyof typeof formData];
      const error = validateField(field, value);
      if (error) newErrors[field] = error;
    }
  });

  // Validate additional locations if any
  formData.additional_locations.forEach((location: AdditionalLocation, index: number) => {
    if (location.address && !location.city) {
      newErrors[`additional_locations.${index}.city`] = 'City is required for additional locations';
    }
    if (location.address && !location.state) {
      newErrors[`additional_locations.${index}.state`] = 'State is required for additional locations';
    }
    if (location.address && !location.zip) {
      newErrors[`additional_locations.${index}.zip`] = 'Zip code is required for additional locations';
    }
  });

  return newErrors;
};

// Check if form can proceed to next step
export const canProceedToStep = (currentStep: number, formData: FormState, risks: { primary_concerns: string[]; location_risks: string[] }): boolean => {
  switch (currentStep) {
    case 1:
      return !!(
        formData.business_name &&
        formData.business_type &&
        formData.industry &&
        formData.employee_count > 0 &&
        formData.annual_revenue &&
        formData.primary_location.address &&
        formData.primary_location.city &&
        formData.primary_location.state &&
        formData.primary_location.zip
      );
    case 2:
      return risks.primary_concerns.length > 0 && risks.location_risks.length > 0;
    case 3:
      // Business Impact Analysis - require at least one BIA field to be completed
      return !!(
        formData.max_downtime ||
        formData.recovery_priority ||
        formData.rpo ||
        formData.financial_impact_severity ||
        formData.operational_impact_severity ||
        formData.reputational_impact_severity ||
        formData.legal_regulatory_impact_severity ||
        (formData.risk_impact_assessments &&
         (Object.keys(formData.risk_impact_assessments.primary_concerns || {}).length > 0 ||
          Object.keys(formData.risk_impact_assessments.location_risks || {}).length > 0))
      );
    case 4:
      return formData.critical_functions.length > 0; // Operations step requires critical functions
    case 5:
      return true; // Mitigations step - can always proceed
    case 6:
      return true; // Review step - validation handled by subscription check
    default:
      return false;
  }
};

// Get validation errors for a specific step
export const getStepValidationErrors = (currentStep: number, formData: FormState): ValidationErrors => {
  const stepErrors: ValidationErrors = {};

  if (currentStep === 1) {
    if (!formData.business_name.trim()) stepErrors.business_name = 'Business name is required';
    if (!formData.business_type.trim()) stepErrors.business_type = 'Business type is required';
    if (!formData.industry.trim()) stepErrors.industry = 'Industry is required';
    if (!formData.employee_count || formData.employee_count <= 0) stepErrors.employee_count = 'Employee count is required';
    if (!formData.annual_revenue) stepErrors.annual_revenue = 'Annual revenue is required';
    if (!formData.primary_location.address.trim()) stepErrors['primary_location.address'] = 'Primary address is required';
    if (!formData.primary_location.city.trim()) stepErrors['primary_location.city'] = 'Primary city is required';
    if (!formData.primary_location.state.trim()) stepErrors['primary_location.state'] = 'Primary state is required';
    if (!formData.primary_location.zip.trim()) stepErrors['primary_location.zip'] = 'Primary zip code is required';
  }

  return stepErrors;
};

// Create field change handler
export const createFieldChangeHandler = (
  formData: FormState,
  setFormData: React.Dispatch<React.SetStateAction<FormState>>,
  errors: ValidationErrors,
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>,
  touched: { [key: string]: boolean },
  setTouched: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>,
  submitError: string | null,
  setSubmitError: React.Dispatch<React.SetStateAction<string | null>>
): FieldChangeHandler => {
  return (field: string, value: FormFieldValue) => {
    setFormData(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        const parentValue = prev[parent as keyof typeof prev];
        if (typeof parentValue === 'object' && parentValue !== null) {
          return {
            ...prev,
            [parent]: {
              ...(parentValue as object),
              [child]: value,
            },
          };
        }
      }
      return { ...prev, [field]: value };
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Clear submit error when user makes changes
    if (submitError) {
      setSubmitError(null);
    }

    // Mark field as touched
    setTouched(prev => ({ ...prev, [field]: true }));
  };
};

// Create field blur handler
export const createFieldBlurHandler = (
  formData: FormState,
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>,
  touched: { [key: string]: boolean },
  setTouched: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>
): FieldBlurHandler => {
  return (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      const parentValue = formData[parent as keyof typeof formData];
      if (typeof parentValue === 'object' && parentValue !== null) {
        const value = (parentValue as any)[child];
        const error = validateField(field, value);
        setErrors(prev => ({ ...prev, [field]: error }));
      }
    } else {
      const value = formData[field as keyof typeof formData];
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };
};

// Check if field has error and should show it
export const shouldShowError = (field: string, touched: { [key: string]: boolean }, errors: ValidationErrors): boolean => {
  return touched[field] && !!errors[field];
};

// Get error message for field
export const getFieldError = (field: string, errors: ValidationErrors): string => {
  return errors[field] || '';
};
